import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const OPEN_DELAY_MS = 300;
const CLOSE_DELAY_MS = 150;

// A nested menu that opens on click, or on hover after a short delay, and closes shortly after the
// pointer leaves both its trigger and itself. Spread `triggerProps` on the trigger, `menuListProps` on the menu list.
export const useHoverSubmenu = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const timer = useRef<number>();

  const clearTimer = useCallback(() => window.clearTimeout(timer.current), []);
  const schedule = useCallback((next: HTMLElement | null, delayMs: number) => {
    clearTimer();
    timer.current = window.setTimeout(() => setAnchorEl(next), delayMs);
  }, [clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  const close = useCallback(() => {
    clearTimer();
    setAnchorEl(null);
  }, [clearTimer]);

  const triggerProps = useMemo(() => ({
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      clearTimer();
      setAnchorEl(event.currentTarget);
    },
    onMouseEnter: (event: React.MouseEvent<HTMLElement>) => schedule(event.currentTarget, OPEN_DELAY_MS),
    onMouseLeave: () => schedule(null, CLOSE_DELAY_MS),
  }), [clearTimer, schedule]);

  const menuListProps = useMemo(() => ({
    onMouseEnter: clearTimer,
    onMouseLeave: () => schedule(null, CLOSE_DELAY_MS),
  }), [clearTimer, schedule]);

  return { anchorEl, close, triggerProps, menuListProps };
};
